/**
 * ResilienceTab — 복원력 패턴 (C018) 라이브 데모
 * POST /api/eraf/platform-lab/resilience/circuit-breaker
 * POST /api/eraf/platform-lab/resilience/retry
 * POST /api/eraf/platform-lab/resilience/timeout
 * 5상태: default / loading / empty / error(503/504) / dialog(N/A — no mutation confirm)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmptyState, ErrorState } from '@ui-kit';
import { useResilienceCb, useResilienceRetry, useResilienceTimeout } from '../hooks/usePlatformLab';
import {
  resilienceCbSchema,
  resilienceRetrySchema,
  resilienceTimeoutSchema,
} from '../schema/platformLab.schema';
import type {
  ResilienceCbFormValues,
  ResilienceRetryFormValues,
  ResilienceTimeoutFormValues,
} from '../schema/platformLab.schema';
import type { ResilienceResponse, CircuitStateSnapshot, ApiError } from '../types/platformLab';

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

function circuitStateColor(state: CircuitState): string {
  if (state === 'CLOSED') return 'var(--color-success, #28a745)';
  if (state === 'OPEN') return 'var(--color-danger)';
  return 'var(--color-warning, #f0ad4e)';
}

function CircuitBadge({ state, t }: { state: CircuitState; t: (k: string) => string }) {
  const color = circuitStateColor(state);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        padding: 'var(--spacing-1) var(--spacing-2)',
        border: `1px solid ${color}`,
        borderRadius: 'var(--radius-sm)',
        color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
      }}
    >
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color }} />
      {t(`platformLab.resilience.state.${state}`)}
    </span>
  );
}

function ResultCard({ result }: { result: ResilienceResponse }) {
  return (
    <div
      style={{
        padding: 'var(--spacing-3)',
        background: result.succeeded ? 'var(--color-success-subtle, #e6f9f0)' : 'var(--color-surface-muted)',
        border: `1px solid ${result.succeeded ? 'var(--color-success, #28a745)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', marginBottom: 'var(--spacing-2)' }}>
        <span><strong>pattern:</strong> {result.pattern}</span>
        <span><strong>succeeded:</strong> {String(result.succeeded)}</span>
        <span><strong>attempts:</strong> {result.attempts}</span>
        <span><strong>elapsedMs:</strong> {result.elapsedMs}</span>
        <span><strong>fallbackUsed:</strong> {String(result.fallbackUsed)}</span>
      </div>
    </div>
  );
}

function CircuitSnapshot({ snapshot, t }: { snapshot: CircuitStateSnapshot; t: (k: string) => string }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', alignItems: 'center', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)' }}>
      <CircuitBadge state={snapshot.state as CircuitState} t={t} />
      <span><strong>{t('platformLab.resilience.cb.failureCount')}:</strong> {snapshot.failureCount} / {snapshot.bufferedCalls}</span>
      <span><strong>failureRate:</strong> {snapshot.failureRate.toFixed(1)}%</span>
    </div>
  );
}

export function ResilienceTab() {
  const { t } = useTranslation();
  const cbMutation = useResilienceCb();
  const retryMutation = useResilienceRetry();
  const timeoutMutation = useResilienceTimeout();

  const [cbResults, setCbResults] = useState<Array<{ result?: ResilienceResponse; error?: ApiError }>>([]);
  const [retryResult, setRetryResult] = useState<ResilienceResponse | null>(null);
  const [retryError, setRetryError] = useState<ApiError | null>(null);
  const [timeoutResult, setTimeoutResult] = useState<ResilienceResponse | null>(null);
  const [timeoutError, setTimeoutError] = useState<ApiError | null>(null);

  const cbForm = useForm<ResilienceCbFormValues>({
    resolver: zodResolver(resilienceCbSchema),
    defaultValues: { failureRate: 100 },
  });
  const retryForm = useForm<ResilienceRetryFormValues>({
    resolver: zodResolver(resilienceRetrySchema),
    defaultValues: { failTimes: 2 },
  });
  const timeoutForm = useForm<ResilienceTimeoutFormValues>({
    resolver: zodResolver(resilienceTimeoutSchema),
    defaultValues: { delayMs: 3000 },
  });

  const onCallCb = cbForm.handleSubmit((values) => {
    cbMutation.mutate({ failureRate: values.failureRate }, {
      onSuccess: (data) => {
        setCbResults((prev) => [...prev, { result: data as ResilienceResponse }]);
      },
      onError: (err) => {
        setCbResults((prev) => [...prev, { error: err as unknown as ApiError }]);
      },
    });
  });

  const onCallRetry = retryForm.handleSubmit((values) => {
    setRetryError(null);
    setRetryResult(null);
    retryMutation.mutate({ failTimes: values.failTimes }, {
      onSuccess: (data) => setRetryResult(data as ResilienceResponse),
      onError: (err) => setRetryError(err as unknown as ApiError),
    });
  });

  const onCallTimeout = timeoutForm.handleSubmit((values) => {
    setTimeoutError(null);
    setTimeoutResult(null);
    timeoutMutation.mutate({ delayMs: values.delayMs }, {
      onSuccess: (data) => setTimeoutResult(data as ResilienceResponse),
      onError: (err) => setTimeoutError(err as unknown as ApiError),
    });
  });

  const hasAnyCbResult = cbResults.length > 0;
  const latestCbEntry = cbResults[cbResults.length - 1];
  const latestCbResult = latestCbEntry?.result;
  const latestCbError = latestCbEntry?.error;
  const circuitIsOpen = latestCbError?.code === 'PLATLAB-503-001' ||
    latestCbResult?.circuit?.state === 'OPEN';

  return (
    <div>
      <h2 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, marginBottom: 'var(--spacing-2)', color: 'var(--color-text-primary)' }}>
        {t('platformLab.resilience.title')}
      </h2>

      {/* 안전 안내 */}
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-5)', fontStyle: 'italic' }}>
        * {t('platformLab.resilience.note.safe')}
      </p>

      {/* ── 서킷브레이커 ────────────────────────────────────────────────── */}
      <section aria-labelledby="cb-section-title" style={{ marginBottom: 'var(--spacing-6)' }}>
        <h3
          id="cb-section-title"
          style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)' }}
        >
          {t('platformLab.resilience.cb.title')}
        </h3>

        <form
          onSubmit={(e) => e.preventDefault()}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-3)' }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('platformLab.resilience.cb.failureRate')}
            <input
              {...cbForm.register('failureRate')}
              type="number"
              min={0}
              max={100}
              aria-label={t('platformLab.resilience.cb.failureRate')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                width: 80,
                fontFamily: 'var(--font-family-sans)',
              }}
            />
          </label>
          <button
            type="button"
            onClick={onCallCb}
            disabled={cbMutation.isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: circuitIsOpen ? 'var(--color-danger)' : 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: cbMutation.isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: cbMutation.isPending ? 0.6 : 1,
            }}
          >
            {cbMutation.isPending ? '...' : t('platformLab.resilience.cb.button')}
          </button>
          {hasAnyCbResult && (
            <button
              type="button"
              onClick={() => setCbResults([])}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'transparent',
                color: 'var(--color-text-muted)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              초기화
            </button>
          )}
        </form>

        {/* loading */}
        {cbMutation.isPending && (
          <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            호출 중...
          </div>
        )}

        {/* empty */}
        {!hasAnyCbResult && !cbMutation.isPending && (
          <EmptyState title={t('platformLab.resilience.empty.title')} />
        )}

        {/* 상태 타임라인 */}
        {hasAnyCbResult && (
          <>
            <div style={{ marginBottom: 'var(--spacing-2)' }}>
              <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                {t('platformLab.resilience.cb.timeline')} ({cbResults.length}회):
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)', alignItems: 'center' }}>
                {cbResults.map((entry, i) => {
                  const state = entry.result?.circuit?.state ?? (entry.error?.code === 'PLATLAB-503-001' ? 'OPEN' : 'CLOSED');
                  return (
                    <span
                      key={i}
                      style={{
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        background: circuitStateColor(state as CircuitState),
                        color: 'var(--color-text-inverse)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                      }}
                    >
                      {state}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* latest result */}
            {latestCbResult && <ResultCard result={latestCbResult} />}
            {latestCbResult?.circuit && <CircuitSnapshot snapshot={latestCbResult.circuit} t={t} />}

            {/* error — 503 서킷 OPEN */}
            {latestCbError && (
              <div style={{ marginTop: 'var(--spacing-2)' }}>
                <ErrorState
                  title={t(`platformLab.error.${latestCbError.code}`, { defaultValue: t('platformLab.error.network') })}
                  message={latestCbError.message}
                  onRetry={() => setCbResults([])}
                />
              </div>
            )}
          </>
        )}
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-surface-border)', margin: 'var(--spacing-4) 0' }} />

      {/* ── 재시도 ────────────────────────────────────────────────────── */}
      <section aria-labelledby="retry-section-title" style={{ marginBottom: 'var(--spacing-6)' }}>
        <h3
          id="retry-section-title"
          style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)' }}
        >
          {t('platformLab.resilience.retry.title')}
        </h3>

        <form
          onSubmit={(e) => e.preventDefault()}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-3)' }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('platformLab.resilience.retry.failTimes')}
            <input
              {...retryForm.register('failTimes')}
              type="number"
              min={0}
              max={10}
              aria-label={t('platformLab.resilience.retry.failTimes')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                width: 80,
                fontFamily: 'var(--font-family-sans)',
              }}
            />
          </label>
          <button
            type="button"
            onClick={onCallRetry}
            disabled={retryMutation.isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: retryMutation.isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: retryMutation.isPending ? 0.6 : 1,
            }}
          >
            {retryMutation.isPending ? '...' : t('platformLab.resilience.retry.button')}
          </button>
        </form>

        {/* loading */}
        {retryMutation.isPending && (
          <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            재시도 중...
          </div>
        )}

        {/* empty */}
        {!retryResult && !retryError && !retryMutation.isPending && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
            failTimes=2 → @Retry maxAttempts=3 → 3번째 시도에서 성공
          </p>
        )}

        {/* error */}
        {retryError && !retryMutation.isPending && (
          <ErrorState
            title={t(`platformLab.error.${retryError.code}`, { defaultValue: t('platformLab.error.network') })}
            message={retryError.message}
            onRetry={() => setRetryError(null)}
          />
        )}

        {/* default */}
        {retryResult && !retryError && !retryMutation.isPending && (
          <>
            <ResultCard result={retryResult} />
            {retryResult.attempts > 1 && (
              <div style={{ marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-success-subtle, #e6f9f0)', border: '1px solid var(--color-success, #28a745)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-success, #28a745)', fontWeight: 600 }}>
                ✓ {t('platformLab.resilience.retry.attempts')}: {retryResult.attempts} ({retryResult.attempts - 1}회 실패 후 성공)
              </div>
            )}
          </>
        )}
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-surface-border)', margin: 'var(--spacing-4) 0' }} />

      {/* ── 타임아웃 ──────────────────────────────────────────────────── */}
      <section aria-labelledby="timeout-section-title">
        <h3
          id="timeout-section-title"
          style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)' }}
        >
          {t('platformLab.resilience.timeout.title')}
        </h3>

        <form
          onSubmit={(e) => e.preventDefault()}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-3)' }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('platformLab.resilience.timeout.delay')}
            <input
              {...timeoutForm.register('delayMs')}
              type="number"
              min={0}
              max={10000}
              aria-label={t('platformLab.resilience.timeout.delay')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                width: 100,
                fontFamily: 'var(--font-family-sans)',
              }}
            />
          </label>
          <button
            type="button"
            onClick={onCallTimeout}
            disabled={timeoutMutation.isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: timeoutMutation.isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: timeoutMutation.isPending ? 0.6 : 1,
            }}
          >
            {timeoutMutation.isPending ? '...' : t('platformLab.resilience.timeout.button')}
          </button>
        </form>

        {/* loading */}
        {timeoutMutation.isPending && (
          <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            대기 중 (최대 1s 후 차단)...
          </div>
        )}

        {/* empty */}
        {!timeoutResult && !timeoutError && !timeoutMutation.isPending && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
            delayMs=3000 → @TimeLimiter 1s 초과 → 504 (PLATLAB-504-001)
          </p>
        )}

        {/* error — 504 */}
        {timeoutError && !timeoutMutation.isPending && (
          <ErrorState
            title={t(`platformLab.error.${timeoutError.code}`, { defaultValue: t('platformLab.error.network') })}
            message={timeoutError.message}
            onRetry={() => setTimeoutError(null)}
          />
        )}

        {/* default — 성공 (delayMs < 1s) */}
        {timeoutResult && !timeoutError && !timeoutMutation.isPending && (
          <ResultCard result={timeoutResult} />
        )}
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
